import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
    forceStatementPaperLeftAligned,
    ONE_INCH_WIDTH,
    PDF_BACKGROUND_COLOR,
    PDF_OFFSCREEN_LEFT,
    STATEMENT_PRINT_PAPER_WIDTH_PX,
} from './statement-pdf.theme';

type Html2CanvasOptions = Parameters<typeof html2canvas>[1];

export type StatementPdfRenderOptions = {
    filename: string;
    format?: 'letter' | 'a4';
    orientation?: 'landscape' | 'portrait';
    unit?: 'in';
    /** Margins in inches: [top, right, bottom, left] */
    margin?: [number, number, number, number];
    canvasScale?: number;
    backgroundColor?: string;
    useCORS?: boolean;
    containerZIndex?: number;
};

/** Must stay in sync with page-boundary simulation in `statement-print-download.ts`. */
export const STATEMENT_PDF_PAGE_FILL_MIN_RATIO = 0.85;

const CONTENT_CUT_SELECTORS =
    'table tbody tr, .ps-grid-row, .ps-grid-total, .ps-codekey, .ps-remit, .ps-footer';

const defaultOpt: Required<Omit<StatementPdfRenderOptions, 'filename'>> = {
    format: 'letter',
    orientation: 'portrait',
    unit: 'in',
    margin: [0, 0, 0, 0],
    canvasScale: 1.5,
    backgroundColor: PDF_BACKGROUND_COLOR,
    useCORS: true,
    containerZIndex: -1,
};

async function waitForFontsAndImages(root: HTMLElement): Promise<void> {
    if (document.fonts?.ready) {
        await document.fonts.ready;
    }

    const imgs = Array.from(root.querySelectorAll('img'));
    await Promise.all(
        imgs.map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise<void>(res => {
                img.addEventListener('load', () => res(), { once: true });
                img.addEventListener('error', () => res(), { once: true });
            });
        }),
    );

    await new Promise(r => requestAnimationFrame(() => r(null)));
}

function pxPerInch(): number {
    const div = document.createElement('div');
    div.style.width = ONE_INCH_WIDTH;
    div.style.position = 'absolute';
    div.style.left = PDF_OFFSCREEN_LEFT;
    document.body.appendChild(div);
    const ppi = div.getBoundingClientRect().width || 96;
    div.remove();
    return ppi;
}

function mountStatementCaptureHost(html: string, opt: Required<StatementPdfRenderOptions>): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = PDF_OFFSCREEN_LEFT;
    wrapper.style.top = '0';
    wrapper.style.width = `${STATEMENT_PRINT_PAPER_WIDTH_PX}px`;
    wrapper.style.zIndex = String(opt.containerZIndex);
    wrapper.style.background = opt.backgroundColor;
    wrapper.style.overflow = 'visible';

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const styleEl = doc.querySelector('style');
    if (styleEl) {
        wrapper.appendChild(styleEl.cloneNode(true));
    }

    const paper = doc.querySelector('.ps-paper');
    if (paper) {
        wrapper.appendChild(paper.cloneNode(true));
    } else {
        Array.from(doc.body.childNodes).forEach(node => {
            wrapper.appendChild(node.cloneNode(true));
        });
    }

    const captureOverrides = document.createElement('style');
    captureOverrides.textContent = `
      .ps-paper {
        width: ${STATEMENT_PRINT_PAPER_WIDTH_PX}px !important;
        max-width: ${STATEMENT_PRINT_PAPER_WIDTH_PX}px !important;
        margin: 0 !important;
        overflow-x: hidden !important;
        overflow-y: visible !important;
        box-sizing: border-box !important;
        padding-bottom: 24px !important;
      }
      .ps-footer {
        padding-bottom: 8px !important;
      }
      .ps-billto > *,
      .ps-remit-box > *,
      .ps-desc { min-width: 0; overflow-wrap: anywhere; }
    `;
    wrapper.appendChild(captureOverrides);
    forceStatementPaperLeftAligned(wrapper);
    return wrapper;
}

/**
 * Render one HTML chunk into the PDF, slicing the canvas into letter pages.
 * Snaps cuts to content-block bottoms so rows/footer are not split mid-block.
 */
async function addHtmlChunkToPdf(params: {
    pdf: jsPDF;
    html: string;
    opt: Required<StatementPdfRenderOptions>;
    isFirstChunk: boolean;
}): Promise<void> {
    const { pdf, html, opt, isFirstChunk } = params;

    const wrapper = mountStatementCaptureHost(html, opt);
    document.body.appendChild(wrapper);
    await waitForFontsAndImages(wrapper);

    const wrapperRect = wrapper.getBoundingClientRect();
    const rowEls = Array.from(wrapper.querySelectorAll(CONTENT_CUT_SELECTORS)) as HTMLElement[];
    const rowBottomsDomPx = rowEls
        .map(el => el.getBoundingClientRect().bottom - wrapperRect.top)
        .filter(v => v > 0)
        .sort((a, b) => a - b);

    const canvas = await html2canvas(wrapper, {
        scale: opt.canvasScale,
        useCORS: opt.useCORS,
        backgroundColor: opt.backgroundColor,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        windowWidth: wrapper.scrollWidth,
        windowHeight: wrapper.scrollHeight,
        height: wrapper.scrollHeight,
        width: wrapper.scrollWidth,
    } as Html2CanvasOptions);

    const pageWidthIn = pdf.internal.pageSize.getWidth();
    const pageHeightIn = pdf.internal.pageSize.getHeight();
    const [mt, mr, mb, ml] = opt.margin;

    const usableWIn = pageWidthIn - ml - mr;
    const usableHIn = pageHeightIn - mt - mb;

    const ppi = pxPerInch();
    const usableWPxAt1x = usableWIn * ppi;
    const scaleToPdf = usableWPxAt1x / canvas.width;
    const usableHPxAt1x = usableHIn * ppi;
    const sliceHeightPx = Math.floor(usableHPxAt1x / scaleToPdf);

    const domToCanvasScaleY = canvas.height / Math.max(wrapperRect.height, wrapper.scrollHeight, 1);
    const rowBottomsCanvasPx = rowBottomsDomPx.map(y => Math.round(y * domToCanvasScaleY));

    const bleedPx = Math.ceil(2 * opt.canvasScale);
    const minSlicePx = Math.floor(sliceHeightPx * STATEMENT_PDF_PAGE_FILL_MIN_RATIO);
    const skipableTailPx = Math.max(bleedPx * 4, Math.ceil(opt.canvasScale * 4));

    let startY = 0;
    let pageIndex = 0;

    while (startY < canvas.height) {
        const idealEnd = Math.min(canvas.height, startY + sliceHeightPx);
        const remainingAfterIdeal = canvas.height - idealEnd;
        const isLastSlice = remainingAfterIdeal <= skipableTailPx;
        const safeEndLimit = isLastSlice ? canvas.height : Math.max(startY + 1, idealEnd - bleedPx);

        let cutY = 0;

        if (isLastSlice) {
            const remainingTotal = canvas.height - startY;
            // Skip paper/canvas padding after the last content block (avoids blank page at chunk end).
            const hasContentAfter = rowBottomsCanvasPx.some(y => y > startY + skipableTailPx);
            if (remainingTotal <= skipableTailPx || !hasContentAfter) {
                break;
            }
            cutY = canvas.height;
        } else {
            for (let i = rowBottomsCanvasPx.length - 1; i >= 0; i--) {
                const y = rowBottomsCanvasPx[i];
                if (y <= safeEndLimit && y > startY + minSlicePx) {
                    cutY = y;
                    break;
                }
            }
            if (!cutY) cutY = idealEnd;
        }

        if (cutY <= startY) {
            if (isLastSlice) {
                break;
            }
            cutY = Math.min(canvas.height, startY + sliceHeightPx);
        }

        if (!isFirstChunk || pageIndex > 0) {
            pdf.addPage();
        }

        const sliceH = cutY - startY;
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceH;

        const ctx = sliceCanvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(canvas, 0, startY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
            const imgData = sliceCanvas.toDataURL('image/jpeg', 0.98);
            const sliceHIn = (sliceH * scaleToPdf) / ppi;
            pdf.addImage(imgData, 'JPEG', ml, mt, usableWIn, sliceHIn, undefined, 'FAST');
        }

        startY = cutY;
        pageIndex++;
    }

    wrapper.remove();
}

/**
 * Append pre-built HTML chunks to a PDF and download.
 * Callers own how rows are split (e.g. ~15 page boundaries).
 */
export async function generateChunkedPdf(params: {
    htmlChunks: string[];
    filename: string;
    jsPdf?: Partial<Pick<StatementPdfRenderOptions, 'format' | 'orientation' | 'unit' | 'margin'>>;
    html2canvas?: Partial<
        Pick<StatementPdfRenderOptions, 'canvasScale' | 'backgroundColor' | 'useCORS'>
    >;
}): Promise<void> {
    const { htmlChunks, filename, jsPdf = {}, html2canvas = {} } = params;

    const opt: Required<StatementPdfRenderOptions> = {
        ...defaultOpt,
        filename,
        ...jsPdf,
        ...html2canvas,
    };

    const pdf = new jsPDF({
        unit: opt.unit,
        format: opt.format,
        orientation: opt.orientation,
        compress: true,
    });

    const chunks = htmlChunks.length ? htmlChunks : [''];

    for (let idx = 0; idx < chunks.length; idx++) {
        await addHtmlChunkToPdf({
            pdf,
            html: chunks[idx],
            opt,
            isFirstChunk: idx === 0,
        });
    }

    pdf.save(filename);
}

/**
 * Capture a live DOM element (e.g. an on-screen statement sheet) into a portrait PDF.
 * Single capture — no activity-row batching. Emotion/CSS class names on the clone resolve
 * against document stylesheets when remounted for html2canvas.
 */
export async function downloadElementAsPdf(params: {
    element: HTMLElement;
    filename: string;
    jsPdf?: Partial<Pick<StatementPdfRenderOptions, 'format' | 'orientation' | 'unit' | 'margin'>>;
    html2canvas?: Partial<
        Pick<StatementPdfRenderOptions, 'canvasScale' | 'backgroundColor' | 'useCORS'>
    >;
}): Promise<void> {
    const { element, filename, jsPdf, html2canvas } = params;
    const html = `<!DOCTYPE html><html><body><div class="ps-paper">${element.outerHTML}</div></body></html>`;
    await generateChunkedPdf({
        htmlChunks: [html],
        filename,
        jsPdf,
        html2canvas,
    });
}
